import { useEffect, useState } from "react";
import { Breadcrumbs, DateTimeDisplay, StatusMessage } from "../../components/ui";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Pagination from "../../components/ui/Pagination";
import SortSummary from "../../components/ui/SortSummary";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { CommsSiteList } from "../../features/comms/CommsSiteList";
import { CommsSiteDetailsPanel, CommsSiteViewDialog } from "../../features/comms/CommsSiteDetails";
import { CommsNoteEditor } from "../../features/comms/CommsNoteEditor";
import { useCommsSites } from "../../features/comms/useCommsSites";
import { useCommsTable } from "../../features/comms/useCommsTable";
import { useCommsNotes } from "../../features/comms/useCommsNotes";
import { COMMS_COLUMNS, getSiteMessage, getSiteSelectionKey, resolveSiteIdentifierRegistryId } from "../../features/comms/commsSite";
import type { CommsSite, CommsType } from "../../features/comms/commsSite";

const CommsDetails = () => {
    const [type, setType] = useState<CommsType>("offline");
    const data = useCommsSites(type);
    const table = useCommsTable(data.sites, type);
    const notes = useCommsNotes(data.revision);
    const [viewSite, setViewSite] = useState<CommsSite | null>(null);
    const [noteSite, setNoteSite] = useState<CommsSite | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const breadcrumbs = useBreadcrumbs({ currentPageTitle: "Comms Details" });
    const { load } = notes;
    useEffect(() => {
        if (data.loading) {return;}
        const targets = type === "offline" ? table.paged : table.selectedSite ? [table.selectedSite] : [];
        targets.forEach(site => {
            const id = resolveSiteIdentifierRegistryId(site);
            if (id !== null) {void load(id);}
        });
    }, [table.paged, table.selectedSite, data.loading, type, load]);
    const historyFor = (site: CommsSite) => {
        const id = resolveSiteIdentifierRegistryId(site);
        return id === null ? { notes: [], loading: false, error: "Unable to determine site identifier for message history." }
            : notes.histories[id] ?? notes.emptyHistory;
    };
    const messageFor = (site: CommsSite) => {
        const history = historyFor(site);
        return history.notes[0]?.message || (getSiteMessage(site) !== "—" ? getSiteMessage(site) : history.error ? "Unavailable" : "—");
    };
    const view = (site: CommsSite) => { table.setSelectedId(getSiteSelectionKey(site)); setViewSite(site); };
    const addMessage = (site: CommsSite) => {
        table.setSelectedId(getSiteSelectionKey(site));
        setNoteSite(site);
        const id = resolveSiteIdentifierRegistryId(site);
        if (id !== null) {void load(id);}
    };
    const activeColumn = COMMS_COLUMNS.find(column => column.key === table.column);
    return (
        <main><div className="container mb-2">
            <section className="page">
                <h2 className="heading__title heading__title--comms-details">Comms Details</h2>
                <DateTimeDisplay /><Breadcrumbs items={breadcrumbs} />
                {success && <StatusMessage kind="success" onClose={() => setSuccess(null)}>{success}</StatusMessage>}
                {warning && <StatusMessage kind="error" onClose={() => setWarning(null)}>{warning}</StatusMessage>}
                {notes.userError && <StatusMessage kind="error">{notes.userError}</StatusMessage>}
                {data.error && <StatusMessage kind="error">{data.error}</StatusMessage>}
                {viewSite && <CommsSiteViewDialog site={viewSite} message={messageFor(viewSite)} onClose={() => setViewSite(null)} />}
                {noteSite && <CommsNoteEditor site={noteSite} history={historyFor(noteSite)} users={notes.users}
                    onReload={id => load(id, true)} onClose={() => setNoteSite(null)}
                    onSaved={(message, error) => {
                        setNoteSite(null);
                        setSuccess(error ? null : message);
                        setWarning(error ? `${message} Refreshing messages failed: ${error}` : null);
                    }} />}
                {data.loading && <LoadingSpinner message={`Loading ${type} sites...`} />}
                <div className="alarm-content">
                    <div className="alarm-content--heading">
                        <div>
                            <h3 className="heading heading__title--comms-details">{table.total} {type === "offline" ? "Offline" : "Online"} Sites</h3>
                            {table.hasAppliedSort && activeColumn && <SortSummary columnLabel={activeColumn.label} sortOrder={table.order}
                                onReset={table.reset} resetAriaLabel="Reset sorting to newest first" />}
                        </div>
                        <div className="comms-filters">
                            <label htmlFor="commsTypeSelect">Online / Offline</label>
                            <select id="commsTypeSelect" className="form__control w-auto" value={type}
                                onChange={event => { setType(event.target.value === "online" ? "online" : "offline"); table.setCurrentPage(1); }}>
                                <option value="offline">Offline</option><option value="online">Online</option>
                            </select>
                        </div>
                    </div>
                    {!data.loading && <div className="admin-requests">
                        <div className="admin-requests__table">
                            <CommsSiteList sites={table.paged} selectedSite={table.selectedSite} type={type}
                                column={table.column} order={table.order} onSort={table.sort} onSelect={table.setSelectedId}
                                onView={view} onAddMessage={addMessage} getMessage={messageFor} />
                        </div>
                        <div className="admin-requests__panel">
                            {table.selectedSite ? <CommsSiteDetailsPanel site={table.selectedSite} message={messageFor(table.selectedSite)}
                                history={historyFor(table.selectedSite)} users={notes.users} offline={type === "offline"}
                                onView={view} onAddMessage={addMessage} /> : <div>Select a site to view details.</div>}
                        </div>
                    </div>}
                </div>
            </section>
            {!data.loading && table.totalPages > 1 && <div className="mt-2">
                <Pagination currentPage={table.page} totalPages={table.totalPages} onPageChange={table.setCurrentPage} />
            </div>}
        </div></main>
    );
};

export default CommsDetails;
